<template>
    <Head title="Reports" />

    <AuthenticatedLayout>
        <template #header>
            <Navigation />
        </template>
        <div class="py-8 max-w-7xl mx-auto grid gap-4">
            <div class="bg-white border border-gray-200 sm:rounded-lg pt-2">
                <div class="grid grid-cols-5 gap-4 border-b font-semibold py-2 px-6">
                    <div>Type</div>
                    <div>Create</div>
                    <div class="text-center">Status</div>
                    <div>Completed for</div>
                    <div class="text-right">Download</div>
                </div>

                <div v-for="data of reports.data" class="grid grid-cols-5 gap-4 items-center border-b py-2 px-6">
                    <div class="font-semibold">{{ data.type }}</div>
                    <div>{{ moment(data.created_at).format('L LTS') }}</div>
                    <div class="mx-auto">
                        <svg v-if="data.status === 'complete'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="text-green-400" viewBox="0 0 16 16">
                            <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
                        </svg>
                        <svg v-else-if="data.status === 'fail'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="text-red-400" viewBox="0 0 16 16">
                            <title>{{ data.error }}</title>
                            <path fill-rule="evenodd" d="M13.854 2.146a.5.5 0 0 1 0 .708l-11 11a.5.5 0 0 1-.708-.708l11-11a.5.5 0 0 1 .708 0Z"/>
                            <path fill-rule="evenodd" d="M2.146 2.146a.5.5 0 0 0 0 .708l11 11a.5.5 0 0 0 .708-.708l-11-11a.5.5 0 0 0-.708 0Z"/>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="text-blue-400" viewBox="0 0 16 16">
                            <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                            <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                        </svg>
                    </div>
                    <div v-if="data.status === 'complete'">
                        {{ data.interval }}
                        <span>second</span>
                    </div>
                    <div v-if="data.status === 'complete'" class="ml-auto">
                        <div v-if="Array.isArray(data.formats)" class="rounded-l">
                            <Button v-if="this.$page.props.auth.permissions.reports.format_json"
                                    @click="download('json', data.id)"
                                    :class="{
                                        'rounded-r-none': this.$page.props.auth.permissions.reports.format_csv || this.$page.props.auth.permissions.reports.format_xlsx
                                    }">json</Button>
                            <Button v-if="this.$page.props.auth.permissions.reports.format_csv"
                                    @click="download('csv', data.id)"
                                    :class="{
                                        'rounded-l-none': this.$page.props.auth.permissions.reports.format_json,
                                        'rounded-r-none': this.$page.props.auth.permissions.reports.format_xlsx
                                    }">csv</Button>
                            <Button v-if="this.$page.props.auth.permissions.reports.format_xlsx"
                                    @click="download('xlsx', data.id)"
                                    :class="{
                                        'rounded-l-none': this.$page.props.auth.permissions.reports.format_json || this.$page.props.auth.permissions.reports.format_csv
                                    }">xlsx</Button>
                        </div>
                        <div class="flex gap-2" v-else>
                            <a
                                class="inline-flex items-center px-4 py-2.5 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray transition ease-in-out duration-150 justify-center"
                                :href="route('report.download', {file: link})"
                                v-for="(link, k, i) of data.formats"
                            >
                                {{ k }}
                            </a>
                        </div>
                    </div>
                </div>

                <Pagination class="my-4" :links="reports.links" />
            </div>
        </div>

    </AuthenticatedLayout>
</template>

<script>
import {Head, Link, useForm} from '@inertiajs/inertia-vue3';
import moment from 'moment';
import AuthenticatedLayout from '@/Layouts/Authenticated';
import Navigation from '@/Components/Navigation/Report';
import Pagination from "@/Components/Pagination";
import Label from "@/Components/Label";
import Input from "@/Components/Input";
import Button from "@/Components/Button";

export default {
    components: {
        Head, Link,
        AuthenticatedLayout,
        Navigation,
        Pagination,
        Label,
        Input,
        Button,
    },
    data () {
        return {
            timer: ''
        }
    },
    created () {
        this.timer = setInterval(this.fetchUpdate, 15000);
    },
    props: {
        reports: Object,
    },
    methods: {
        moment,
        download(type, id) {
            window.location.href = route('report.'+type, id);
        },
        fetchUpdate () {
            this.$inertia.reload({
                preserveState: false,
                preserveScroll: false,
            })
        },
    },
    beforeUnmount () {
        clearInterval(this.timer);
    }
}
</script>

